import React, { useEffect, useState } from "react";
import localeText from "../../../../locale";
import { Button } from "primereact/button";
import { useToggle } from "../../../../hooks/useToggle";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import CreateTransactionDialog from "../CreateTransactionDialog/CreateTransactionDialog";
import SubmitProofDialog from "../SubmitProofDialog/SubmitProofDialog";
import PhysicalStoreService from "../../../../service/PhysicalStoreService";
import { QRCodeCanvas } from "qrcode.react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatDate } from "../../../../utilities/formatDate";
import { formatPrice } from "../../../../utilities/formatPrice";
import "../../pages/FinancialHub/components/FinancialTransactionsTable/FinancialTransactionsTable.css";
import { Dialog } from "primereact/dialog";

const StoreTransactions = ({ user, currency }) => {
  const [isLoadingCreateTransaction, setIsLoadingCreateTransaction] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const [isLoadingSubmitProof, setIsLoadingSubmitProof] = useState(false);
  const [verificationIsPending, setVerificationIsPending] = useState(false);
  const [verificationRejectionReason, setVerificationRejectionReason] = useState(null);
  const [transactionObj, setTransactionObj] = useState();
  const [isLoadingTransaction, setIsLoadingTransaction] = useState(false);
  const [isLoadingGetAllPayments, setIsLoadingGetAllPayments] = useState(true);
  const [transactions, setTransactions] = useState();

  const { showSuccessToast, showErrorToast } = useToastContext();

  const {
    value: isOpenCreateTransactionModal,
    setAsTrue: openCreateTransactionModal,
    setAsFalse: closeCreateTransactionModal,
  } = useToggle();

  const {
    value: isOpenQrCodeTransactionModal,
    setAsTrue: openOpenQrCodeTransactionModal,
    setAsFalse: closeOpenQrCodeTransactionModal,
  } = useToggle();

  const {
    value: isOpenSubmitProofModal,
    setAsTrue: openSubmitProofModal,
    setAsFalse: closeSubmitProofModal,
  } = useToggle();

  const physicalStoreService = new PhysicalStoreService();

  const handleCreateTransaction = async ({ amount, description }) => {
    setIsLoadingCreateTransaction(true);

    try {
      const res = await physicalStoreService.createTransaction({
        amount,
        description,
        user: user.username,
      });

      if (res.messageCode === 200000) {
        closeCreateTransactionModal();
        setTransactionObj(res.result);
        handleOpenQrCodeTransactionModal(res.result);
        return;
      }

      showErrorToast({ code: res.messageCode });
    } catch (error) {
      console.error("Error creating transaction:", error);
    } finally {
      setIsLoadingCreateTransaction(false);
    }
  };

  const handleOpenNewTransaction = async () => {
    setIsCheckingVerification(true);
    try {
      const res = await physicalStoreService.verificationStatus(user.username);

      if (res.messageCode === 500253) {
        setVerificationIsPending(res.result?.status === "PENDING");
        openSubmitProofModal();

        return;
      }

      if (res.messageCode === 200001 && res.result?.verificationStatus === "PENDING") {
        setVerificationIsPending(true);
        openSubmitProofModal();
        return;
      }

      if (res.messageCode === 200001 && res.result?.verificationStatus === "APPROVED") {
        openCreateTransactionModal();
        return;
      }

      if (res.messageCode === 200001 && res.result?.verificationStatus === "REJECTED") {
        setVerificationRejectionReason(res.result?.rejectionReason || null);
        openSubmitProofModal();
      }
    } catch (error) {
      console.error("Error checking verification:", error);
      showErrorToast({ code: 500000 });
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const handleSubmitProof = async (formData) => {
    setIsLoadingSubmitProof(true);
    try {
      const res = await physicalStoreService.submitProof({ ...formData, username: user.username });
      if (res.messageCode === 200000) {
        setVerificationIsPending(true);
        showSuccessToast({ detail: "physicalStoreProofSubmitted" });
      } else {
        showErrorToast({ code: res.messageCode });
      }
    } catch (error) {
      console.error("Error submitting proof:", error);
    } finally {
      setIsLoadingSubmitProof(false);
    }
  };

  const handleOpenQrCodeTransactionModal = async (transaction) => {
    if (typeof transaction === "object") {
      openOpenQrCodeTransactionModal();
      return;
    }

    setIsLoadingTransaction(true);

    openOpenQrCodeTransactionModal();
    try {
      const res = await physicalStoreService.getPayment(transaction);

      if (res.messageCode !== 200001) {
        showErrorToast({ code: res.messageCode });
        return;
      }

      setTransactionObj(res.result);
    } catch (error) {
      console.error("Error fetching transaction:", error);
    } finally {
      setIsLoadingTransaction(false);
    }
  };

  const getAllPayments = async () => {
    setIsLoadingGetAllPayments(true);

    try {
      const res = await physicalStoreService.getAllPayments();

      if (res.messageCode !== 200001) {
        showErrorToast({ code: res.messageCode });
        return;
      }

      setTransactions(res.result);
    } catch (error) {
      console.error("Error fetching transaction:", error);
    } finally {
      setIsLoadingGetAllPayments(false);
    }
  };

  const handleCloseQrCodeTransactionModal = () => {
    setTransactionObj(undefined);
    closeOpenQrCodeTransactionModal();
  };

  useEffect(() => {
    const verifyIfTransactionIsPaid = async () => {
      if (isOpenQrCodeTransactionModal && transactionObj?.id) {
        const res = await physicalStoreService.getPayment(transactionObj?.id);

        if (res.messageCode !== 200001) {
          showErrorToast({ code: res.messageCode });
          return;
        }

        if (res.result?.dateTimePayment) {
          setTransactionObj(res.result);
          handleCloseQrCodeTransactionModal();
          showSuccessToast({ detail: "transactionDone" });
          await getAllPayments();
        }
      }
    };

    const interval = setInterval(() => {
      verifyIfTransactionIsPaid();
    }, 3000);

    if (!isOpenQrCodeTransactionModal) clearInterval(interval);

    return () => clearInterval(interval); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenQrCodeTransactionModal, transactionObj]);

  useEffect(() => {
    if (user) getAllPayments(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <>
      <CreateTransactionDialog
        visible={isOpenCreateTransactionModal}
        onHide={closeCreateTransactionModal}
        onSubmit={handleCreateTransaction}
        loading={isLoadingCreateTransaction}
        currency={currency}
        title={localeText("createChargeWithQrCode")}
        subtitle={localeText("createChargeWithQrCodeDescription")}
      />

      <SubmitProofDialog
        visible={isOpenSubmitProofModal}
        onHide={closeSubmitProofModal}
        onSubmit={handleSubmitProof}
        loading={isLoadingSubmitProof}
        isPending={verificationIsPending}
        rejectionReason={verificationRejectionReason}
      />

      <Dialog
        header={localeText("qrCodeOfTransaction")}
        visible={isOpenQrCodeTransactionModal}
        className="w-30rem m-2"
        onHide={handleCloseQrCodeTransactionModal}
        draggable={false}
      >
        {isLoadingTransaction ? (
          <div className="flex justify-content-center align-items-center py-4">
            <i
              className="pi pi-spin pi-spinner text-3xl"
              style={{ color: "var(--theme-text-muted)" }}
            />
          </div>
        ) : (
          transactionObj?.id && (
            <div className="flex flex-column justify-content-center align-items-center gap-3 py-2">
              <div
                className="border-round p-3"
                style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}
              >
                <QRCodeCanvas value={`${transactionObj.id}`} size={200} />
              </div>
              <p className="m-0 text-sm" style={{ color: "var(--theme-text-muted)" }}>
                {localeText("awaitingPayment")}...
              </p>
              <p className="m-0 font-semibold" style={{ color: "var(--theme-text)" }}>
                {currency} {formatPrice(transactionObj.amountPaid)}
              </p>
            </div>
          )
        )}
      </Dialog>

      <div>
        <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div className="flex align-items-center gap-3">
            <div
              className="border-circle flex align-items-center justify-content-center"
              style={{
                border: "1px solid var(--theme-border, #EBEBEB)",
                width: "54px",
                height: "54px",
                flexShrink: 0,
              }}
            >
              <i
                className="pi pi-credit-card text-xl"
                style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
              />
            </div>
            <h2 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text)" }}>
              {localeText("transactions")}
            </h2>
          </div>

          <Button
            label={localeText("transaction")}
            icon="pi pi-plus"
            className="border-round-md border-none p-no-outline"
            style={{ backgroundColor: "var(--theme-primary, #1DAF61)", color: "#fff" }}
            loading={isCheckingVerification}
            onClick={handleOpenNewTransaction}
          />
        </div>

        <div className="financial-transactions-table">
          <div className="overflow-hidden">
            <DataTable
              value={transactions}
              paginator
              loading={isLoadingGetAllPayments}
              responsiveLayout="scroll"
              className="financial-transactions-datatable"
              paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate={localeText("showingFirstToLast")}
              rows={10}
              rowsPerPageOptions={[10, 20, 50]}
              emptyMessage={
                <div className="flex flex-column align-items-center justify-content-center py-8 gap-3">
                  <div
                    className="border-round flex align-items-center justify-content-center"
                    style={{
                      width: "120px",
                      height: "120px",
                      backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                    }}
                  >
                    <i className="pi pi-credit-card text-6xl text-400" />
                  </div>
                  <p className="m-0" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                    {localeText("noOperationsRecords")}
                  </p>
                </div>
              }
            >
              <Column header="ID" body={(row) => <span className="font-medium">{row.id}</span>} />

              <Column header={localeText("username")} body={(row) => row.seller?.username ?? "—"} />

              <Column
                header={localeText("value")}
                body={(row) => (
                  <span className="font-medium">
                    {currency} {formatPrice(row.amountPaid)}
                  </span>
                )}
              />

              <Column header={localeText("description")} body={(row) => row.description ?? "—"} />

              <Column
                header={localeText("adminBackofficeDateCreation")}
                body={(row) => formatDate(row.dateTime)}
              />

              <Column header={localeText("payer")} body={(row) => row?.buyer?.username ?? "—"} />

              <Column
                header={localeText("status")}
                body={(row) => (
                  <span className="flex align-items-center gap-2">
                    <span
                      className="border-circle"
                      style={{
                        width: "8px",
                        height: "8px",
                        flexShrink: 0,
                        backgroundColor: row.dateTimePayment
                          ? "var(--theme-primary, #1DAF61)"
                          : "#F7B005",
                      }}
                    />
                    {row.dateTimePayment ? localeText("completed") : localeText("awaitingPayment")}
                  </span>
                )}
              />

              <Column
                header={localeText("paymentDate")}
                body={(row) =>
                  row.dateTimePayment ? (
                    formatDate(row.dateTimePayment)
                  ) : (
                    <span style={{ color: "var(--theme-text-muted)" }}>—</span>
                  )
                }
              />

              <Column
                header=""
                body={(row) => (
                  <Button
                    icon="pi pi-qrcode"
                    disabled={!!row.dateTimePayment}
                    onClick={() => handleOpenQrCodeTransactionModal(row.id)}
                    className="border-round-md border-none p-no-outline"
                    style={{
                      backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                      color: "var(--theme-text-muted)",
                    }}
                  />
                )}
                style={{ width: "64px" }}
              />
            </DataTable>
          </div>
        </div>
      </div>
    </>
  );
};

export default StoreTransactions;
